import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { ExtensionListService } from 'src/app/core/service/grid-data/grid-data-extension.service';
import { ExtensionService } from 'src/app/core/service/extension.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { Extension } from '../model/extension';

@Component({
  standalone: false,
  selector: 'app-extension-grid',
  templateUrl: './extension-grid.component.html',
  styleUrls: ['./extension-grid.component.scss']
})
export class ExtensionGridComponent implements OnInit, OnDestroy
{
  @Output() public refreshExtensionTotal = new EventEmitter<number>();

  public pageSizes = [5, 10, 25, 50, 100];

  /**
   * Assessment list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  public filterText = '';
  public enableAdvanced = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public isNew = false;
  public editDataItem: Extension;
  public selectedExtension: Extension;
  public patientLabel = {};
  public deleteDialogOpened = false;
  public categories = [];

  private listSubscription: Subscription;

  constructor(
    private appConfigService: AppConfigService,
    private extensionListService: ExtensionListService,
    private searchHelperService: SearchHelperService,
    private extensionService: ExtensionService,
    private messageHandlerService: MessageHandlerService,
    private settingService: SettingService
  ) { }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.extensionListService.loading;
  }

  public ngOnInit(): void
  {
    this.categories = this.extensionService.extensionCategories;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.categories.find(x => x.key === 'PATIENT').text = sessionSetting.patientLabel;
      }
    });
    this.listSubscription = this.extensionListService.subscribe({
      next: (value) =>
      {
        // Preprocessing data before it's passed to the Kendo Grid.
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.lastModifiedOn = new Date(d.lastModifiedOn);
          }
          );
        }
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.total = value.total : this.total = 0
        };
        this.refreshExtensionTotal.emit(this.total);
      }
    });
    this.searchFieldChange('');
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.extensionListService.queryByState(state);
  }

  /**
   * Filtering by search field
   */
  public filterChange(currentFilter: CompositeFilterDescriptor): void
  {
    this.state.filter = currentFilter;
    this.extensionListService.queryByState(this.state);
  }

  /**
   * Search has been changed.
   *
   * @param filterText Search text.
   */
  public searchFieldChange(filterText: string): void
  {
    this.setState();
    if (filterText !== '')
    {
      const root = this.searchHelperService.fieldChange(filterText, this.state, 'name');
      this.filterText = filterText;
      this.filterChange(root);
    }
    else
    {
      this.filterChange(this.state.filter);
    }
  }

  /**
   * Initialize the proper state before query
   */
  public setState(): void
  {
    this.state = {
      filter: {
        filters: [], logic: 'and'
      },
      group: [],
      skip: 0,
      sort: [
        { field: 'key', dir: 'asc' }
      ],
      take: this.appConfigService.getConfig().pageSize
    };
  }

  public addHandler(): void
  {
    this.isNew = true;
    setTimeout(() =>
    {
      this.editDataItem = new Extension();
    });
  }

  /**
   * Save Extension change.
   *
   * @param extension Extension object.
   */
  public saveChangesHandler(extension: Extension): void
  {
    this.extensionService.saveExtension(extension).subscribe({
      next: () =>
      {
        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('EXTENSION-SAVED-SUCCESSFULLY');

        this.state.skip = 0;
        this.extensionListService.queryByState(this.state);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.extensionService.getExtension(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    // Check if in use
    this.extensionService.isExtensionInUse(id).subscribe({
      next: (inUseResponse) =>
      {
        // If in use, avoid deletion
        if (inUseResponse)
        {
          this.messageHandlerService.displayWarning('EXTENSION-IN-USE-CANNOT-DELETE');
        }
        else
        {
          this.extensionService.getExtension(id).subscribe({
            next: (data) =>
            {
              this.selectedExtension = data;
              this.deleteDialogOpened = true;
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public deleteExtension(): void
  {
    // Proceed with the deletion
    this.extensionService.deleteExtension(this.selectedExtension.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('EXTENSION-DELETED-SUCCESSFULLY');
        this.gridView.data = this.gridView.data.filter(e => e.id !== this.selectedExtension.id);
        // Reload entire grid if there is no more record to display on a page.
        if (this.gridView.data.length === 0 && this.state.skip > 0)
        {
          this.state.skip = 0;
          this.extensionListService.queryByState(this.state);
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.deleteDialogOpened = false;
      }
    });
  }

}
